import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
const COUNTDOWN_SECONDS = 15 * 60; // 15:00
const RESET_AFTER_EXPIRY_MS = 60 * 60 * 1000; // 1 hour grace after expiry
const DEADLINE_KEY = 'pr2026_offer_deadline';
/**
 * Returns the offer deadline (epoch ms). Persists it in localStorage so a page
 * reload keeps counting down from where it was.
 *
 * - No stored deadline → start a fresh 15:00 window and save it.
 * - Stored deadline expired more than 1h ago → treat it as a brand-new visit:
 *   start a fresh 15:00 window and overwrite the stored key.
 * - Stored deadline still in the future → resume counting down to it.
 * - Stored deadline expired less than 1h ago → keep it (already past), so the
 *   bar shows the "oferta cerrada" state and refreshing can't reset the timer.
 */
function resolveDeadline(): number {
  const now = Date.now();
  if (typeof window === 'undefined') return now + COUNTDOWN_SECONDS * 1000;
  try {
    const deadlineRaw = window.localStorage.getItem(DEADLINE_KEY);
    const deadline = deadlineRaw ? parseInt(deadlineRaw, 10) : NaN;
    // First visit (no valid stored deadline) OR expired more than 1h ago:
    // start a fresh 15:00 window.
    if (Number.isNaN(deadline) || now - deadline > RESET_AFTER_EXPIRY_MS) {
      const newDeadline = now + COUNTDOWN_SECONDS * 1000;
      window.localStorage.setItem(DEADLINE_KEY, String(newDeadline));
      return newDeadline;
    }
    // Still counting down, or expired within the last hour: resume/keep it.
    return deadline;
  } catch {
    // localStorage unavailable (private mode, etc.) — fall back to a fresh timer.
    return now + COUNTDOWN_SECONDS * 1000;
  }
}
function format(secondsLeft: number): string {
  const clamped = Math.max(0, secondsLeft);
  const mm = String(Math.floor(clamped / 60)).padStart(2, '0');
  const ss = String(clamped % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}
export function StickyAlertBar() {
  const [secondsLeft, setSecondsLeft] = useState<number>(COUNTDOWN_SECONDS);
  useEffect(() => {
    const deadline = resolveDeadline();
    const tick = () => {
      const remaining = Math.round((deadline - Date.now()) / 1000);
      setSecondsLeft(Math.max(0, remaining));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);
  const isExpired = secondsLeft <= 0;
  return <div className="sticky top-0 z-50 w-full border-b-2 border-gold-dark bg-crimson-dark text-white" role="status" aria-live="polite" data-id="element-274" data-mp="3ryfmc0dzoyar">
      <div className="mx-auto flex max-w-5xl items-center justify-center gap-2 px-4 py-2 text-center" data-id="element-275" data-mp="okv3gv7h7d25">
        {isExpired ? <p className="text-xs font-700 uppercase leading-tight tracking-wide sm:text-sm" data-id="element-276" data-mp="jmjtmkh6l2jx">
            Oferta cerrada por ahora
          </p> : <motion.p animate={{
        opacity: [1, 0.55, 1]
      }} transition={{
        duration: 2.2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="text-xs font-700 uppercase leading-tight tracking-wide sm:text-sm" data-id="element-277" data-mp="34br1b5tooppx">
            <span aria-hidden="true" data-id="element-278" data-mp="1jswy9qyza17k">⚠ </span>OFERTA TERMINA EN{' '}
            <span className="tabular-nums" data-id="element-279" data-mp="1oqp2nkgt7fsn">{format(secondsLeft)}</span> —
            Después el precio sube a $432.990
          </motion.p>}
      </div>
    </div>;
}